import { useEffect, useState, useCallback } from "react";
import { api, STATUS_STYLES, STATUS_LABELS, fmtTime } from "@/lib/api";
import { toast } from "sonner";
import { Siren, ArrowsClockwise, EnvelopeSimple, WhatsappLogo, FloppyDisk, MapPin, XCircle } from "@phosphor-icons/react";

const Field = ({ label, value, onChange, missing, testid, mono }) => (
  <div>
    <label className={`text-xs font-bold uppercase tracking-[0.2em] ${missing ? "text-red-400" : "text-zinc-500"}`}>
      {label} {missing && "• MISSING"}
    </label>
    <input
      className={`tactical-input mt-1 ${mono ? "font-mono-data text-xs" : ""} ${missing ? "border-red-500/50" : ""}`}
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value)}
      data-testid={testid}
    />
  </div>
);

const LivePill = ({ secondsAgo }) => (
  <div
    style={{
      position: "fixed", bottom: 20, right: 24, zIndex: 30,
      padding: "8px 14px", background: "rgba(26,30,36,0.94)", color: "#fff",
      borderRadius: 999, fontSize: 11, fontFamily: "'JetBrains Mono', ui-monospace, monospace",
      letterSpacing: "0.14em", textTransform: "uppercase",
      display: "flex", alignItems: "center", gap: 8,
      boxShadow: "0 8px 24px rgba(0,0,0,0.2)",
      pointerEvents: "none",
    }}
    data-testid="live-indicator"
  >
    <span style={{ width: 7, height: 7, borderRadius: "50%", background: "#1E9E63", boxShadow: "0 0 0 3px rgba(30,158,99,0.28)", animation: "live-pulse 2s ease-in-out infinite" }} />
    LIVE · updated {secondsAgo}s ago
  </div>
);

export default function IncidentsPage() {
  const [incidents, setIncidents] = useState([]);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState("");
  const [closing, setClosing] = useState(false);
  const [closeReason, setCloseReason] = useState("false_alarm");
  const [closeNote, setCloseNote] = useState("");
  const [lastPoll, setLastPoll] = useState(Date.now());
  const [, forceTick] = useState(0);
  const [folder, setFolder] = useState("inbox"); // "inbox" | "closed"

  const load = useCallback(async (keepId) => {
    const r = await api.get("/incidents");
    setIncidents(r.data);
    setLastPoll(Date.now());
    if (keepId) {
      const found = r.data.find((i) => i.id === keepId);
      if (found) select(found);
    } else if (r.data.length && !selected) {
      const first = r.data.find((i) => i.status !== "closed") || r.data[0];
      select(first);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { load(); }, [load]);

  // Live-poll every 8s. The open incident's LIVE state (status, CSG number, timestamps) is refreshed
  // too — it used to look stuck on "received" until the operator clicked it again — but the fields
  // the operator is typing into are never touched.
  useEffect(() => {
    const id = setInterval(async () => {
      try {
        const r = await api.get("/incidents");
        setIncidents(r.data);
        setLastPoll(Date.now());
        setSelected((cur) => {
          if (!cur) return cur;
          const fresh = r.data.find((i) => i.id === cur.id);
          return fresh ? { ...cur, ...fresh } : cur;
        });
      } catch (e) { console.error("Incident poll failed (keeping last good data)", e); }
    }, 8000);
    return () => clearInterval(id);
  }, []);

  // Tick every 5s so the "updated Xs ago" label stays honest.
  useEffect(() => {
    const id = setInterval(() => forceTick((t) => t + 1), 5000);
    return () => clearInterval(id);
  }, []);

  const secondsAgo = Math.max(0, Math.floor((Date.now() - lastPoll) / 1000));

  const select = (inc) => {
    setSelected(inc);
    setForm({
      person_name: inc.person_name, cell: inc.cell, address: inc.address,
      lat: inc.lat, lng: inc.lng, description: inc.description,
      end_client_name: inc.end_client_name, sop: inc.sop,
      requester_name: inc.requester_name, requester_phone: inc.requester_phone,
    });
  };

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const act = async (name, fn, successMsg) => {
    setBusy(name);
    try {
      const r = await fn();
      select(r.data);
      await load(r.data.id);
      toast.success(successMsg(r.data));
    } catch (e) {
      toast.error("Action failed", { description: e.response?.data?.detail || e.message });
    } finally {
      setBusy("");
    }
  };

  const save = () =>
    act("save", () => {
      const payload = { ...form };
      if (payload.lat !== null && payload.lat !== undefined && payload.lat !== "") payload.lat = parseFloat(payload.lat);
      if (payload.lng !== null && payload.lng !== undefined && payload.lng !== "") payload.lng = parseFloat(payload.lng);
      return api.patch(`/incidents/${selected.id}`, payload);
    }, () => "Incident details saved");

  const dispatch = () =>
    act("dispatch", () => api.post(`/incidents/${selected.id}/dispatch`),
      (d) => `Dispatch Sent — CSG Connect ${d.csg_incident_no || "logged"}`);

  const requestDetails = () =>
    act("request", () => api.post(`/incidents/${selected.id}/request-details`),
      () => "Missing-details request emailed to sender");

  const refresh = () =>
    act("refresh", () => api.post(`/incidents/${selected.id}/refresh`),
      (d) => `CSG Connect Updated — status: ${d.csg_status || d.status}`);

  const submitClose = () =>
    act("close", () => api.post(`/incidents/${selected.id}/close`, { reason: closeReason, note: closeNote || null }),
      (d) => `Incident closed — ${d.csg_outcome || "manual"}`)
      .then(() => { setClosing(false); setCloseNote(""); setCloseReason("false_alarm"); });

  const missing = selected?.missing_fields || [];

  const inboxCount = incidents.filter((i) => i.status !== "closed").length;
  const closedCount = incidents.length - inboxCount;
  const visible = incidents.filter((i) => (folder === "inbox" ? i.status !== "closed" : i.status === "closed"));

  return (
    <div className="flex h-screen" data-testid="incidents-page">
      <div className="w-80 shrink-0 border-r border-zinc-800 overflow-y-auto">
        <div className="px-4 py-4 border-b border-zinc-800 sticky top-0 bg-[#0c0c0e]/90 backdrop-blur z-10">
          <h1 className="font-heading text-xl font-black tracking-tighter uppercase">Incidents</h1>
          <div className="flex gap-1 mt-3" data-testid="incident-folder-tabs">
            {[
              { key: "inbox", label: "Inbox", count: inboxCount },
              { key: "closed", label: "Closed", count: closedCount },
            ].map((t) => {
              const active = folder === t.key;
              return (
                <button
                  key={t.key}
                  onClick={() => setFolder(t.key)}
                  data-testid={`folder-tab-${t.key}`}
                  style={{
                    padding: "6px 12px",
                    fontSize: 11,
                    fontFamily: "'JetBrains Mono', ui-monospace, monospace",
                    letterSpacing: "0.14em",
                    textTransform: "uppercase",
                    borderRadius: 6,
                    border: active ? "1px solid rgba(224,26,46,0.35)" : "1px solid rgba(26,30,36,0.10)",
                    background: active ? "rgba(224,26,46,0.08)" : "transparent",
                    color: active ? "#E01A2E" : "#5B6B7E",
                    cursor: "pointer",
                    fontWeight: active ? 700 : 500,
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6,
                    transition: "background 0.15s, color 0.15s, border-color 0.15s",
                  }}
                >
                  {t.label}
                  <span style={{ fontVariantNumeric: "tabular-nums", opacity: 0.7 }}>{t.count}</span>
                </button>
              );
            })}
          </div>
        </div>
        {visible.length === 0 && (
          <div className="p-6 text-zinc-600 text-sm font-mono-data" data-testid="folder-empty-state">
            {folder === "inbox" ? "INBOX CLEAR. NO ACTIVE INCIDENTS." : "NO CLOSED INCIDENTS YET."}
          </div>
        )}
        {visible.map((i) => (
          <button
            key={i.id}
            onClick={() => select(i)}
            data-testid={`incident-row-${i.ref}`}
            className={`w-full text-left px-4 py-3 border-b border-zinc-800 hover:bg-zinc-900 transition-colors ${
              i.source === "portal" ? "bg-orange-500/[0.07] border-l-2 border-l-orange-500" : ""
            } ${selected?.id === i.id ? "bg-zinc-900 border-l-2 border-l-[#007AFF]" : ""}`}
          >
            <div className="flex justify-between items-center">
              <span className="font-mono-data text-sm flex items-center gap-2">
                {i.ref}
                {i.source === "portal" && (
                  <span className="text-[9px] font-bold px-1.5 py-0.5 border rounded-sm bg-orange-500/10 text-orange-400 border-orange-500/20">PORTAL</span>
                )}
                {i.sla_breach && (
                  <span className="text-[9px] font-bold px-1.5 py-0.5 border rounded-sm bg-red-500/15 text-red-400 border-red-500/30">SLA</span>
                )}
              </span>
              <span className={`text-[9px] font-bold px-1.5 py-0.5 border rounded-sm ${STATUS_STYLES[i.status]}`}>
                {STATUS_LABELS[i.status]}
              </span>
            </div>
            <div className="text-xs text-zinc-400 mt-1 truncate">{i.client_name || "Unidentified sender"}</div>
            <div className="text-[11px] text-zinc-600 truncate">{i.subject || i.description}</div>
            <div className="font-mono-data text-[10px] text-zinc-600 mt-1">{fmtTime(i.received_at)}</div>
          </button>
        ))}
      </div>

      <div className="flex-1 overflow-y-auto">
        {!selected ? (
          <div className="h-full flex items-center justify-center text-zinc-600 font-mono-data">SELECT AN INCIDENT</div>
        ) : (
          <div className="p-8" data-testid="incident-detail">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div>
                <h2 className="font-heading text-3xl font-black tracking-tighter font-mono-data">{selected.ref}</h2>
                <div className="text-sm text-zinc-400 mt-1">
                  {selected.client_name || "Unidentified sender"} · <span className="font-mono-data">{selected.sender_email}</span>
                </div>
              </div>
              <div className="flex items-center gap-3">
                {selected.sla_breach && (
                  <span className="text-xs font-bold px-3 py-1 border rounded-sm bg-red-500/15 text-red-400 border-red-500/30" data-testid="incident-sla-badge">
                    SLA BREACH — {selected.dispatch_minutes} MIN (TARGET {selected.sla_target})
                  </span>
                )}
                {selected.source === "portal" && (
                  <span className="text-xs font-bold px-3 py-1 border rounded-sm bg-orange-500/10 text-orange-400 border-orange-500/20" data-testid="incident-portal-badge">
                    PORTAL SUBMISSION
                  </span>
                )}
                <span className={`text-xs font-bold px-3 py-1 border rounded-sm ${STATUS_STYLES[selected.status]}`} data-testid="incident-status-badge">
                  {STATUS_LABELS[selected.status]}
                </span>
                {selected.csg_incident_no && (
                  <span className="font-mono-data text-xs text-[#007AFF] border border-[#007AFF]/30 bg-[#007AFF]/10 px-3 py-1 rounded-sm" data-testid="incident-csg-ref">
                    CSG {selected.csg_incident_no}
                  </span>
                )}
                {selected.csg_step_label && selected.status !== "closed" && (
                  <span className="text-xs font-bold px-3 py-1 border rounded-sm bg-amber-500/10 text-amber-400 border-amber-500/30" data-testid="incident-csg-step">
                    {selected.csg_step_label.toUpperCase()}
                    {selected.csg_status_at ? ` · ${fmtTime(selected.csg_status_at)}` : ""}
                  </span>
                )}
              </div>
            </div>

            <div className="flex flex-wrap gap-2 mt-6">
              <button onClick={save} disabled={!!busy} className="btn-tactical bg-zinc-800 hover:bg-zinc-700 text-white flex items-center gap-2" data-testid="save-button">
                <FloppyDisk size={14} weight="bold" /> Save Details
              </button>
              {missing.length > 0 && selected.client_name && (
                <button onClick={requestDetails} disabled={!!busy} className="btn-tactical bg-yellow-500/15 border border-yellow-500/30 text-yellow-400 hover:bg-yellow-500/25 flex items-center gap-2" data-testid="request-details-button">
                  <EnvelopeSimple size={14} weight="bold" /> Request Missing Details
                </button>
              )}
              {selected.status !== "dispatched" && selected.status !== "closed" && (
                <button onClick={dispatch} disabled={!!busy} className="btn-tactical bg-[#FF3B30] hover:bg-[#d32f27] text-white flex items-center gap-2" data-testid="dispatch-button">
                  <Siren size={14} weight="bold" /> {busy === "dispatch" ? "Dispatching..." : "Dispatch to CSG Connect"}
                </button>
              )}
              {selected.csg_incident_id && selected.status !== "closed" && (
                <button onClick={refresh} disabled={!!busy} className="btn-tactical bg-zinc-800 hover:bg-zinc-700 text-white flex items-center gap-2" data-testid="refresh-status-button">
                  <ArrowsClockwise size={14} weight="bold" /> Refresh CSG Status
                </button>
              )}
              {selected.csg_incident_id && (form.lat != null || form.address) && (
                <button
                  onClick={() => act("push-loc", () => api.post(`/incidents/${selected.id}/push-location`).then((r) => api.get(`/incidents/${selected.id}`)), "Location re-pushed to CSG")}
                  disabled={!!busy}
                  className="btn-tactical bg-zinc-800 hover:bg-zinc-700 text-white flex items-center gap-2"
                  data-testid="push-location-button"
                >
                  <MapPin size={14} weight="bold" /> {busy === "push-loc" ? "Pushing..." : "Push Location to CSG"}
                </button>
              )}
              {selected.status !== "closed" && (
                <button
                  onClick={() => setClosing(true)}
                  disabled={!!busy}
                  className="btn-tactical flex items-center gap-2"
                  style={{
                    background: "rgba(91,107,126,0.10)",
                    color: "#5B6B7E",
                    border: "1px solid rgba(91,107,126,0.28)",
                  }}
                  data-testid="close-incident-button"
                >
                  <XCircle size={14} weight="bold" /> Close incident
                </button>
              )}
            </div>

            {closing && (
              <div
                className="fixed inset-0 z-50 flex items-center justify-center px-4"
                style={{ background: "rgba(26,30,36,0.55)", backdropFilter: "blur(4px)" }}
                data-testid="close-modal"
                onClick={(e) => { if (e.target === e.currentTarget) setClosing(false); }}
              >
                <div className="w-full max-w-md bg-white rounded-lg p-7" style={{ border: "1px solid rgba(26,30,36,0.10)", boxShadow: "0 20px 60px rgba(0,0,0,0.18)" }}>
                  <h3
                    style={{
                      fontFamily: "'Fraunces', Georgia, serif",
                      fontWeight: 500,
                      fontSize: 26,
                      letterSpacing: "-0.02em",
                      color: "#1A1E24",
                      margin: 0,
                      marginBottom: 6,
                    }}
                  >
                    Close <em style={{ fontStyle: "italic", color: "#5B6B7E", fontWeight: 400 }}>{selected.ref}</em>?
                  </h3>
                  <p style={{ color: "#5B6B7E", fontSize: 13, marginBottom: 22, lineHeight: 1.5 }}>
                    Use this for false alarms, tests, and duplicates that shouldn&apos;t be dispatched. The incident will be marked closed with your reason attached.
                  </p>

                  <label style={{ fontSize: 10, fontFamily: "'JetBrains Mono', monospace", letterSpacing: "0.18em", textTransform: "uppercase", color: "#5B6B7E" }}>
                    Reason
                  </label>
                  <select
                    value={closeReason}
                    onChange={(e) => setCloseReason(e.target.value)}
                    className="tactical-input mt-2"
                    data-testid="close-reason-select"
                  >
                    <option value="false_alarm">False alarm</option>
                    <option value="test">Test incident</option>
                    <option value="duplicate">Duplicate</option>
                    <option value="other">Other</option>
                  </select>

                  <label style={{ fontSize: 10, fontFamily: "'JetBrains Mono', monospace", letterSpacing: "0.18em", textTransform: "uppercase", color: "#5B6B7E", display: "block", marginTop: 16 }}>
                    Note (optional)
                  </label>
                  <textarea
                    value={closeNote}
                    onChange={(e) => setCloseNote(e.target.value)}
                    className="tactical-input mt-2"
                    style={{ minHeight: 72, resize: "vertical" }}
                    placeholder="Add context so this is auditable later…"
                    data-testid="close-note-input"
                  />

                  <div className="flex justify-end gap-3 mt-6">
                    <button
                      onClick={() => setClosing(false)}
                      className="btn-tactical"
                      style={{ background: "transparent", color: "#5B6B7E", border: "1px solid rgba(26,30,36,0.14)" }}
                      data-testid="close-cancel"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={submitClose}
                      disabled={busy === "close"}
                      className="btn-tactical text-white"
                      style={{ background: "#1A1E24" }}
                      data-testid="close-confirm"
                    >
                      {busy === "close" ? "Closing…" : "Close incident"}
                    </button>
                  </div>
                </div>
              </div>
            )}

            <div className="grid md:grid-cols-2 gap-4 mt-8 border border-zinc-800 bg-[#121214] p-6">
              <Field label="Requested By (client contact)" value={form.requester_name} onChange={set("requester_name")} missing={false} testid="field-requester-name" />
              <Field label="Requester Tel" value={form.requester_phone} onChange={set("requester_phone")} missing={false} testid="field-requester-phone" mono />
              <Field label="Person Name" value={form.person_name} onChange={set("person_name")} missing={missing.includes("person_name")} testid="field-person-name" />
              <Field label="Cell Number" value={form.cell} onChange={set("cell")} missing={missing.includes("cell")} testid="field-cell" mono />
              <div className="md:col-span-2">
                <label className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">Google Maps Link or Coordinates <span className="text-zinc-600 normal-case tracking-normal">(paste — auto-fills address, lat & lng)</span></label>
                <input
                  className="tactical-input mt-1 font-mono-data text-xs"
                  placeholder="https://maps.app.goo.gl/... or -26.2041, 28.0473"
                  value={form.gpaste ?? ""}
                  onChange={(e) => set("gpaste")(e.target.value)}
                  onPaste={async (e) => {
                    const text = e.clipboardData.getData("text");
                    if (!text) return;
                    set("gpaste")(text);
                    try {
                      const r = await api.post("/geocode/paste", { text });
                      if (r.data.lat != null && r.data.lng != null) {
                        setForm((f) => ({ ...f, lat: r.data.lat, lng: r.data.lng, address: f.address || r.data.address || f.address }));
                        toast.success("Location extracted", { description: `${r.data.lat}, ${r.data.lng}${r.data.address ? " · " + r.data.address.slice(0, 60) : ""}` });
                      } else {
                        toast.error("Could not read a location from that paste", { description: "Try coordinates or a Google Maps link" });
                      }
                    } catch (err) {
                      toast.error("Location lookup failed", { description: err.response?.data?.detail || err.message });
                    }
                  }}
                  data-testid="field-gpaste"
                />
              </div>
              <Field label="Address" value={form.address} onChange={set("address")} missing={missing.includes("location") && !form.lat} testid="field-address" />
              <div className="grid grid-cols-2 gap-3">
                <Field label="Lat" value={form.lat} onChange={set("lat")} missing={false} testid="field-lat" mono />
                <Field label="Lng" value={form.lng} onChange={set("lng")} missing={false} testid="field-lng" mono />
              </div>
              <Field label="End Client" value={form.end_client_name} onChange={set("end_client_name")} missing={false} testid="field-end-client" />
              <div className="md:col-span-2">
                <label className={`text-xs font-bold uppercase tracking-[0.2em] ${missing.includes("description") ? "text-red-400" : "text-zinc-500"}`}>
                  Situation Description {missing.includes("description") && "• MISSING"}
                </label>
                <textarea className="tactical-input mt-1 min-h-[90px]" value={form.description ?? ""} onChange={(e) => set("description")(e.target.value)} data-testid="field-description" />
              </div>
              <div className="md:col-span-2">
                <label className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">SOP (inserted into CSG incident body)</label>
                <textarea className="tactical-input mt-1 min-h-[110px] font-mono-data text-xs" value={form.sop ?? ""} onChange={(e) => set("sop")(e.target.value)} data-testid="field-sop" />
              </div>
            </div>

            {selected.csg_timeline?.length > 0 && (
              <div className="border border-zinc-800 bg-[#121214] p-6 mt-6" data-testid="csg-progress-section">
                <div className="flex items-center justify-between">
                  <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">
                    CSG Connect progress · live from the command centre
                  </h3>
                  <span className="text-[10px] font-mono-data text-zinc-500">polling every 20s</span>
                </div>
                <div className="mt-4 space-y-0">
                  {selected.csg_timeline.map((s, i) => (
                    <div key={`${s.status}-${i}`} className="flex items-start gap-3 py-2 border-b border-zinc-800/60 last:border-0" data-testid={`csg-step-${i}`}>
                      <span className={`mt-[6px] w-2 h-2 rounded-full shrink-0 ${i === selected.csg_timeline.length - 1 ? "bg-[#FF3B30]" : "bg-zinc-600"}`} />
                      <div className="min-w-0 flex-1">
                        <div className="text-sm text-white font-semibold">{s.label || s.status}</div>
                        {s.by && <div className="text-[11px] text-zinc-500">{s.by}</div>}
                      </div>
                      <span className="font-mono-data text-[11px] text-zinc-400 shrink-0">{fmtTime(s.at)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {(selected.status === "closed" || selected.csg_terminal_status) && (
              <div className="border border-green-500/30 bg-green-500/5 p-6 mt-6" data-testid="closure-section">
                <div className="flex items-center justify-between">
                  <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-green-400">CSG Connect Closure</h3>
                  <span className="text-[10px] font-bold px-2 py-0.5 border border-green-500/30 bg-green-500/10 text-green-400 rounded-sm uppercase font-mono-data" data-testid="closure-status">
                    {selected.csg_terminal_status || selected.csg_status || "closed"}
                  </span>
                </div>
                <div className="grid sm:grid-cols-2 gap-4 mt-3 text-sm">
                  <div><span className="text-zinc-500 text-xs uppercase tracking-wider">Closed at</span><div className="font-mono-data text-white text-xs mt-0.5">{selected.closed_at ? fmtTime(selected.closed_at) : "—"}</div></div>
                  <div><span className="text-zinc-500 text-xs uppercase tracking-wider">Closed by</span><div className="font-mono-data text-white text-xs mt-0.5">{selected.csg_closed_by || "—"}</div></div>
                  <div><span className="text-zinc-500 text-xs uppercase tracking-wider">Outcome</span><div className="font-mono-data text-white text-xs mt-0.5">{selected.csg_outcome || "—"}</div></div>
                  <div><span className="text-zinc-500 text-xs uppercase tracking-wider">Attended by</span><div className="font-mono-data text-white text-xs mt-0.5">{selected.csg_contractor || "—"}</div></div>
                </div>
                <div className="mt-4">
                  <div className="text-zinc-500 text-xs uppercase tracking-wider mb-1">Closing comments (from CSG Connect)</div>
                  {selected.csg_comments ? (
                    <pre className="whitespace-pre-wrap font-sans text-sm text-white bg-black/30 border border-zinc-800 p-3" data-testid="closure-comments">{selected.csg_comments}</pre>
                  ) : (
                    <div className="text-zinc-500 text-xs italic" data-testid="closure-no-comments">
                      CSG Connect did not publish closing comments for this incident. Hit "Refresh CSG Status" to re-poll.
                    </div>
                  )}
                </div>
              </div>
            )}

            {selected.whatsapp_links?.length > 0 && (
              <div className="border border-zinc-800 bg-[#121214] p-6 mt-6" data-testid="whatsapp-section">
                <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500 mb-3">WhatsApp Crew Alerts — one click per responder</h3>
                <div className="flex flex-wrap gap-2">
                  {selected.whatsapp_links.map((w) => (
                    <a key={w.crew_id} href={w.url} target="_blank" rel="noopener noreferrer"
                      className="btn-tactical bg-green-500/10 border border-green-500/30 text-green-400 hover:bg-green-500/20 flex items-center gap-2 no-underline"
                      data-testid={`whatsapp-link-${w.crew_id}`}>
                      <WhatsappLogo size={16} weight="bold" /> {w.name} · <span className="font-mono-data">{w.whatsapp}</span>
                    </a>
                  ))}
                </div>
              </div>
            )}

            <div className="border border-zinc-800 bg-[#121214] p-6 mt-6">
              <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500 mb-3">Activity / Notifications</h3>
              {(!selected.notifications || selected.notifications.length === 0) && (
                <div className="text-zinc-600 text-sm font-mono-data">NO NOTIFICATIONS LOGGED</div>
              )}
              {selected.notifications?.map((n) => (
                <div key={n.id} className="flex justify-between items-center py-2 border-b border-zinc-800 last:border-0 text-sm" data-testid={`notification-${n.id}`}>
                  <div>
                    <span className="text-white">{n.subject}</span>
                    <span className="text-zinc-500 text-xs ml-2">→ {n.to}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-sm border ${n.status === "sent" ? "bg-green-500/10 text-green-400 border-green-500/20" : "bg-yellow-500/10 text-yellow-400 border-yellow-500/20"}`}>
                      {n.status.toUpperCase()}
                    </span>
                    <span className="font-mono-data text-[10px] text-zinc-600">{fmtTime(n.at)}</span>
                  </div>
                </div>
              ))}
            </div>

            <div className="border border-zinc-800 bg-[#121214] p-6 mt-6">
              <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500 mb-3">Original Email</h3>
              <div className="font-mono-data text-xs text-zinc-400 whitespace-pre-wrap bg-[#09090b] border border-zinc-800 p-4" data-testid="raw-email-body">
                {selected.raw_body}
              </div>
            </div>
          </div>
        )}
      </div>
      <LivePill secondsAgo={secondsAgo} />
    </div>
  );
}
